import React, { useState, useEffect } from 'react';
import { pitchEventAPI, adminAPI } from '../lib/api';

const CreatePitchEventModal = ({ isOpen, onClose, onSuccess, initialData = null, isEdit = false }) => {
    const [formData, setFormData] = useState({
        eventName: '',
        description: '',
        eventType: 'PITCH_COMPETITION',
        eventDate: '',
        registrationDeadline: '',
        location: '',
        maxParticipants: '',
        judges: '',
        sponsors: '',
        organizerOption: 'existing', // 'existing' or 'create'
        organizerId: '',
        newOrganizerName: '',
        newOrganizerEmail: '',
        newOrganizerPassword: ''
    });
    const [organizers, setOrganizers] = useState([]);
    const [loadingOrganizers, setLoadingOrganizers] = useState(false);

    useEffect(() => {
        if (isOpen && initialData && isEdit) {
            setFormData({
                eventName: initialData.eventName || '',
                description: initialData.description || '',
                eventType: initialData.eventType || 'PITCH_COMPETITION',
                eventDate: initialData.eventDate || '',
                registrationDeadline: initialData.registrationDeadline || '',
                location: initialData.location || '',
                maxParticipants: initialData.maxParticipants || '',
                judges: initialData.judges || '',
                sponsors: initialData.sponsors || '',
                organizerOption: 'existing',
                organizerId: initialData.organizer?.id || '',
                newOrganizerName: '',
                newOrganizerEmail: '',
                newOrganizerPassword: ''
            });
        } else if (isOpen && !isEdit) {
            setFormData({
                eventName: '',
                description: '',
                eventType: 'PITCH_COMPETITION',
                eventDate: '',
                registrationDeadline: '',
                location: '',
                maxParticipants: '',
                judges: '',
                sponsors: '',
                organizerOption: 'existing',
                organizerId: '',
                newOrganizerName: '',
                newOrganizerEmail: '',
                newOrganizerPassword: ''
            });
        }
    }, [isOpen, initialData, isEdit]);

    useEffect(() => {
        if (isOpen && formData.organizerOption === 'existing') {
            loadOrganizers();
        }
    }, [isOpen, formData.organizerOption]);

    const loadOrganizers = async () => {
        setLoadingOrganizers(true);
        try {
            const data = await adminAPI.getEventOrganizers();
            setOrganizers(data || []);
        } catch (error) {
            console.error('Failed to load organizers:', error);
            setOrganizers([]);
        } finally {
            setLoadingOrganizers(false);
        }
    };
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dateErrors, setDateErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        // Clear date errors when user changes the date
        if (name === 'eventDate' || name === 'registrationDeadline') {
            setDateErrors({});
        }
    };

    const validateDates = () => {
        const errors = {};
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Validate event date
        if (formData.eventDate) {
            const eventDate = new Date(formData.eventDate);
            eventDate.setHours(0, 0, 0, 0);

            if (eventDate < today) {
                errors.eventDate = 'Event date cannot be in the past';
            }
        }

        // Validate registration deadline
        if (formData.registrationDeadline) {
            const regDeadline = new Date(formData.registrationDeadline);
            regDeadline.setHours(0, 0, 0, 0);

            if (regDeadline < today) {
                errors.registrationDeadline = 'Registration deadline cannot be in the past';
            }

            // Check if registration deadline is before event date
            if (formData.eventDate) {
                const eventDate = new Date(formData.eventDate);
                eventDate.setHours(0, 0, 0, 0);

                if (regDeadline >= eventDate) {
                    errors.registrationDeadline = 'Registration deadline must be before the event date';
                }
            }
        }

        setDateErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Validate dates before submission
        if (!validateDates()) {
            setError('Please fix the date validation errors before submitting');
            setLoading(false);
            return;
        }

        try {
            let organizerId = null;

            // Handle organizer assignment
            if (formData.organizerOption === 'create') {
                // Create new organizer user first
                if (!formData.newOrganizerName || !formData.newOrganizerEmail || !formData.newOrganizerPassword) {
                    setError('Please fill in all fields for the new organizer');
                    setLoading(false);
                    return;
                }

                try {
                    const newOrganizer = await adminAPI.createUser({
                        name: formData.newOrganizerName,
                        email: formData.newOrganizerEmail,
                        password: formData.newOrganizerPassword,
                        role: 'EVENT_ORGANIZER'
                    });
                    organizerId = newOrganizer.id;
                } catch (err) {
                    setError('Failed to create organizer: ' + (err.response?.data?.message || err.message));
                    setLoading(false);
                    return;
                }
            } else {
                // Use existing organizer
                if (!formData.organizerId) {
                    setError('Please select an event organizer');
                    setLoading(false);
                    return;
                }
                organizerId = formData.organizerId;
            }

            const payload = {
                eventName: formData.eventName,
                description: formData.description,
                eventType: formData.eventType,
                eventDate: formData.eventDate,
                registrationDeadline: formData.registrationDeadline,
                location: formData.location,
                maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
                judges: formData.judges,
                sponsors: formData.sponsors,
                organizerId: organizerId
            };

            if (isEdit) {
                await pitchEventAPI.updateEvent(initialData.id, payload);
            } else {
                await pitchEventAPI.createEvent(payload);
            }

            if (!isEdit) {
                setFormData({
                    eventName: '',
                    description: '',
                    eventType: 'PITCH_COMPETITION',
                    eventDate: '',
                    registrationDeadline: '',
                    location: '',
                    maxParticipants: '',
                    judges: '',
                    sponsors: '',
                    organizerOption: 'existing',
                    organizerId: '',
                    newOrganizerName: '',
                    newOrganizerEmail: '',
                    newOrganizerPassword: ''
                });
            }
            onSuccess();
            onClose();
        } catch (err) {
            setError(err.message || (isEdit ? 'Failed to update event' : 'Failed to create pitch event'));
        } finally {
            setLoading(false);
        }
    };

    // Calculate max date for registration deadline (one day before event date)
    const getMaxRegistrationDeadline = () => {
        if (!formData.eventDate) return undefined;
        const eventDate = new Date(formData.eventDate);
        eventDate.setDate(eventDate.getDate() - 1);
        return eventDate.toISOString().split('T')[0];
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 rounded-t-xl">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold text-white">{isEdit ? 'Edit Event' : 'Create Pitch Event'}</h2>
                        <button onClick={onClose} className="text-white hover:text-gray-200 transition">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                            {error}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Event Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="eventName"
                            value={formData.eventName}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Annual Startup Pitch Competition 2024"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Event Description
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="3"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Describe the event, what startups can expect, judging criteria, etc."
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Event Type
                            </label>
                            <select
                                name="eventType"
                                value={formData.eventType}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="PITCH_COMPETITION">Pitch Competition</option>
                                <option value="DEMO_DAY">Demo Day</option>
                                <option value="INVESTOR_MEETUP">Investor Meetup</option>
                                <option value="WORKSHOP">Workshop</option>
                                <option value="NETWORKING">Networking Event</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Event Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="eventDate"
                                value={formData.eventDate ? new Date(formData.eventDate).toISOString().split('T')[0] : ''}
                                onChange={handleChange}
                                onBlur={validateDates}
                                min={new Date().toISOString().split('T')[0]}
                                required
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                    dateErrors.eventDate ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                            {dateErrors.eventDate && (
                                <p className="text-red-500 text-xs mt-1">{dateErrors.eventDate}</p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Registration Deadline
                            </label>
                            <input
                                type="date"
                                name="registrationDeadline"
                                value={formData.registrationDeadline ? new Date(formData.registrationDeadline).toISOString().split('T')[0] : ''}
                                onChange={handleChange}
                                onBlur={validateDates}
                                min={new Date().toISOString().split('T')[0]}
                                max={getMaxRegistrationDeadline()}
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                    dateErrors.registrationDeadline ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                            {dateErrors.registrationDeadline && (
                                <p className="text-red-500 text-xs mt-1">{dateErrors.registrationDeadline}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Location <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Convention Center, City"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Max Participants
                        </label>
                        <input
                            type="number"
                            name="maxParticipants"
                            value={formData.maxParticipants}
                            onChange={handleChange}
                            min="1"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="50"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Judges
                        </label>
                        <input
                            type="text"
                            name="judges"
                            value={formData.judges}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Comma-separated names"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Sponsors
                        </label>
                        <input
                            type="text"
                            name="sponsors"
                            value={formData.sponsors}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Comma-separated sponsor names"
                        />
                    </div>

                    {/* Event Organizer Section */}
                    <div className="border-t pt-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Organizer</h3>
                        
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Organizer Option
                            </label>
                            <div className="flex space-x-4">
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name="organizerOption"
                                        value="existing"
                                        checked={formData.organizerOption === 'existing'}
                                        onChange={handleChange}
                                        className="mr-2"
                                    />
                                    <span className="text-sm text-gray-700">Select Existing Organizer</span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name="organizerOption"
                                        value="create"
                                        checked={formData.organizerOption === 'create'}
                                        onChange={handleChange}
                                        className="mr-2"
                                    />
                                    <span className="text-sm text-gray-700">Create New Organizer</span>
                                </label>
                            </div>
                        </div>

                        {formData.organizerOption === 'existing' ? (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Select Organizer <span className="text-red-500">*</span>
                                </label>
                                {loadingOrganizers ? (
                                    <div className="flex items-center justify-center py-4">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                    </div>
                                ) : (
                                    <select
                                        name="organizerId"
                                        value={formData.organizerId}
                                        onChange={handleChange}
                                        required={formData.organizerOption === 'existing'}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">-- Select Event Organizer --</option>
                                        {organizers.map((organizer) => (
                                            <option key={organizer.id} value={organizer.id}>
                                                {organizer.name} ({organizer.email})
                                            </option>
                                        ))}
                                    </select>
                                )}
                                {organizers.length === 0 && !loadingOrganizers && (
                                    <p className="text-sm text-gray-500 mt-2">
                                        No event organizers found. Please create one first or choose "Create New Organizer".
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Organizer Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="newOrganizerName"
                                        value={formData.newOrganizerName}
                                        onChange={handleChange}
                                        required={formData.organizerOption === 'create'}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="John Doe"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Organizer Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="newOrganizerEmail"
                                        value={formData.newOrganizerEmail}
                                        onChange={handleChange}
                                        required={formData.organizerOption === 'create'}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="organizer@example.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Password <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        name="newOrganizerPassword"
                                        value={formData.newOrganizerPassword}
                                        onChange={handleChange}
                                        required={formData.organizerOption === 'create'}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Minimum 8 characters"
                                        minLength="8"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        The organizer will use this email and password to login
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>{isEdit ? 'Updating...' : 'Creating...'}</span>
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    <span>{isEdit ? 'Update Event' : 'Create Event'}</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreatePitchEventModal;
